import { commonUtils } from '@/helpers/commonUtils'
import styles from './PptPageMomentResults.module.less'
import { Divider } from 'antd'
import MarkdownRenderer from '@/components/MarkdownRenderer/MarkdownRenderer'
import ChartRangeView from '@/pages/ProjectDetailPage/components/StoryboardAnalysis/components/ChartRangeView/ChartRangeView'
import LegendDot from '@/pages/ProjectDetailPage/components/VideoAnalysis/VideoAnalysisChart/components/LegendDot/LegendDot'
import { useEffect, useRef, useState } from 'react'
import { genRangeDesc } from '@/pages/ProjectDetailPage/components/StoryboardAnalysis/StoryboardAnalysis'
import { useTranslation } from 'react-i18next'
import PptPage, { PageHeader } from '../PptPage/PptPage'
import { SliderModels } from '@/types/slider'
import html2canvas from 'html2canvas'
import { useSize } from 'ahooks'
import { isPortraitByProjectInfo } from '@/helpers/productCategory'
import cls from 'classnames'

type MomentKey = keyof ProjectModels.ProjectDetail['dimensionMetrics']

interface Props {
  project: ProjectModels.ProjectDetail
  momentKey: MomentKey
  onSliderReady: (slider: SliderModels.Slider, no: number) => void
  no: number
}

const getRangeDesc = (key: MomentKey, project: ProjectModels.ProjectDetail) => {
  if (key === 'firstThree') {
    const startTime = project.dimensionMetrics.firstThree.startTime
    const endTime = project.dimensionMetrics.firstThree.endTime
    return genRangeDesc({ startTime, endTime })
  }
  if (key === 'lastThree') {
    const startTime = project.dimensionMetrics.lastThree.startTime
    const endTime = project.dimensionMetrics.lastThree.endTime
    return genRangeDesc({ startTime, endTime })
  }

  const dimensionMetrics = project.dimensionMetrics[key]
  if (dimensionMetrics.periods) {
    return dimensionMetrics.periods
      .map((range) => {
        return genRangeDesc({ startTime: range.startTime, endTime: range.endTime })
      })
      .join('、')
  }
  return ''
}

const genData = (key: MomentKey, project: ProjectModels.ProjectDetail) => {
  if (key === 'firstThree') {
    const firstThreeDimension = project.dimensionMetrics.firstThree
    return {
      score: firstThreeDimension.score,
      meanScore: firstThreeDimension.meanScore,
      results: firstThreeDimension.reason.performance,
      ranges: [
        {
          start: firstThreeDimension.startTime,
          end: firstThreeDimension.endTime,
        },
      ],
    }
  }
  if (key === 'lastThree') {
    const lastThreeDimension = project.dimensionMetrics.lastThree
    return {
      score: lastThreeDimension.score,
      meanScore: lastThreeDimension.meanScore,
      results: lastThreeDimension.reason.performance,
      ranges: [
        {
          start: lastThreeDimension.startTime,
          end: lastThreeDimension.endTime,
        },
      ],
    }
  }
  const dimensionMetrics = project.dimensionMetrics[key]
  return {
    score: dimensionMetrics.overall.score,
    meanScore: dimensionMetrics.overall.meanScore,
    results: dimensionMetrics.overall.reason.performance,
    ranges: dimensionMetrics.periods
      .sort((a, b) => a.order - b.order)
      .map((item) => ({
        start: item.startTime,
        end: item.endTime,
      })),
  }
}

function PptPageMomentResults({ project, momentKey, onSliderReady, no }: Props) {
  const BOX_MAX_HEIGHT = 550
  const [ratio, setRatio] = useState<number>(1)
  const [ratioFinished, setRatioFinished] = useState<boolean>(false)
  const { t, i18n } = useTranslation()
  const isJaLocale = (i18n.resolvedLanguage || i18n.language || '').toLowerCase().startsWith('ja')
  const normBoxWidth = isJaLocale ? 360 : 264
  const resultsRef = useRef<HTMLDivElement>(null)
  const titleMap: {
    [key in MomentKey]: string
  } = {
    firstThree: t('dashboard.projectDetailPage.storyboardView.theGolden3Seconds'),
    keyInformation: t('dashboard.projectDetailPage.storyboardView.informationTransmission'),
    brandConnection: t('dashboard.projectDetailPage.storyboardView.brandRelevance'),
    interestingEnagement: t('dashboard.projectDetailPage.storyboardView.interestStimulation'),
    lastThree: t('dashboard.projectDetailPage.storyboardView.theEnding3Seconds'),
  }
  const rangeDesc = getRangeDesc(momentKey, project)
  const dimensionMetrics = genData(momentKey, project)
  const isPortraitLayout = isPortraitByProjectInfo(project)
  const adDuration = Number(project.adDuration || 0)
  const detailSeconds = project.detailMetrics.length
  const isPortraitRegularCreative =
    isPortraitLayout &&
    (project.durationCategory === 'ten_plus' || adDuration > 10 || detailSeconds > 10)
  const isPortraitSplashCreative = isPortraitLayout && !isPortraitRegularCreative
  const [chartRendered, setChartRendered] = useState(false)
  const chartRef = useRef<HTMLDivElement>(null)
  const [chartImage, setChartImage] = useState<string>()
  const titleRef = useRef<HTMLDivElement>(null)
  const descRef = useRef<HTMLDivElement>(null)
  const normBoxRef = useRef<HTMLDivElement>(null)
  const normCntRef = useRef<HTMLSpanElement>(null)
  const normDescRef = useRef<HTMLDivElement>(null)
  const keyResultRef = useRef<HTMLDivElement>(null)
  const keyTitleIconRef = useRef<HTMLImageElement>(null)
  const keyTitleTextRef = useRef<HTMLSpanElement>(null)

  const [basicSlider, setBasicSlider] = useState<SliderModels.Slider>()
  const [elements, setElements] = useState<SliderModels.PPTElement[]>([])
  const [pageHeaderElements, setPageHeaderElements] = useState<SliderModels.PPTElement[]>([])
  const [pageRect, setPageRect] = useState<DOMRect>()

  const [resultChildren, setResultChildren] = useState<HTMLCollection>()

  const resultSize = useSize(resultsRef.current)

  const onPageRender = (slider: SliderModels.Slider, pageRect: DOMRect) => {
    setBasicSlider(slider)
    setPageRect(pageRect)
  }
  const onPageHeaderRender = (elements: SliderModels.PPTElement[]) => {
    setPageHeaderElements(elements)
  }

  useEffect(() => {
    if (
      !titleRef.current ||
      !descRef.current ||
      !normBoxRef.current ||
      !normCntRef.current ||
      !normDescRef.current ||
      !keyResultRef.current ||
      !keyTitleIconRef.current ||
      !keyTitleTextRef.current ||
      !chartRef.current ||
      !chartImage ||
      !resultChildren
    ) {
      return
    }
    const titleRect = titleRef.current.getBoundingClientRect()
    const descRect = descRef.current.getBoundingClientRect()
    const keyResultRect = keyResultRef.current.getBoundingClientRect()
    const keyTitleIconRect = keyTitleIconRef.current.getBoundingClientRect()
    const keyTitleTextRect = keyTitleTextRef.current.getBoundingClientRect()
    const chartRect = chartRef.current.getBoundingClientRect()
    const normBoxRect = normBoxRef.current.getBoundingClientRect()
    const normCntRect = normCntRef.current.getBoundingClientRect()
    const normDescRect = normDescRef.current.getBoundingClientRect()
    const normDescWidth = Math.max(
      normDescRect.width + 24,
      normBoxRect.right - normDescRect.x - 16
    )

    // 20 是 border-radiu, 700 是最大高度
    /*     const SHAPE_RATIO = 20 / 700
    const SHAPE_WIDTH = keyResultRect.width
    const SHAPE_HEIGHT = keyResultRect.height
    const SHAPE_HEIGHT_RATIO = SHAPE_HEIGHT * SHAPE_RATIO
    const SHAPE_WIDTH_DELTA = SHAPE_WIDTH - SHAPE_HEIGHT_RATIO
    const SHAPE_HEIGHT_DELTA = SHAPE_HEIGHT - SHAPE_HEIGHT_RATIO */

    function generateShapeInfo(rect: DOMRect, radio: number, height: number) {
      const SHAPE_RATIO = radio / height
      const SHAPE_WIDTH = rect.width
      const SHAPE_HEIGHT = rect.height
      const SHAPE_HEIGHT_RATIO = SHAPE_HEIGHT * SHAPE_RATIO
      const SHAPE_WIDTH_DELTA = SHAPE_WIDTH - SHAPE_HEIGHT_RATIO
      const SHAPE_HEIGHT_DELTA = SHAPE_HEIGHT - SHAPE_HEIGHT_RATIO
      return {
        shapeRatio: SHAPE_RATIO,
        shapeWidth: SHAPE_WIDTH,
        shapeHeight: SHAPE_HEIGHT,
        shapeHeightRatio: SHAPE_HEIGHT_RATIO,
        shapeWidthDelta: SHAPE_WIDTH_DELTA,
        shapeHeightDelta: SHAPE_HEIGHT_DELTA,
      }
    }
    const shapeInfo1 = generateShapeInfo(keyResultRect, 20, 700)
    const shapeInfo2 = generateShapeInfo(normBoxRect, 16, 72)

    const elements: SliderModels.PPTElement[] = [
      {
        type: 'text',
        content: titleRef.current.outerHTML,
        x: titleRect.x,
        y: titleRect.y - 70,
        width: titleRect.width,
        height: titleRect.height,
      },
      {
        type: 'text',
        content: descRef.current.outerHTML,
        x: descRect.x,
        y: descRect.y - 70,
        width: descRect.width,
        height: descRect.height,
      },
      {
        type: 'shape',
        x: keyResultRect.x,
        y: keyResultRect.y,
        width: keyResultRect.width,
        height: keyResultRect.height + keyResultRef.current.children.length * 10,
        viewBox: [keyResultRect.width, keyResultRect.height],
        path: `M ${shapeInfo1.shapeHeightRatio} 0 L ${shapeInfo1.shapeWidthDelta} 0 Q ${shapeInfo1.shapeWidth} 0 ${shapeInfo1.shapeWidth} ${shapeInfo1.shapeHeightRatio} L ${shapeInfo1.shapeWidth} ${shapeInfo1.shapeHeightDelta} Q ${shapeInfo1.shapeWidth} ${shapeInfo1.shapeHeight} ${shapeInfo1.shapeWidthDelta} ${shapeInfo1.shapeHeight} L ${shapeInfo1.shapeHeightRatio} ${shapeInfo1.shapeHeight} Q 0 ${shapeInfo1.shapeHeight} 0 ${shapeInfo1.shapeHeightDelta} L 0 ${shapeInfo1.shapeHeightRatio} Q 0 0 ${shapeInfo1.shapeHeightRatio} 0 Z`,
        fixedRatio: false,
        rotate: 0,
        pathFormula: SliderModels.ShapePathFormulasKeys.ROUND_RECT,
        keypoints: [shapeInfo1.shapeRatio],
        fill: '#f0f0ff',
        // NOTE: 这里注释掉是因为 pptxgenjs 不支持 gradient 的设置
        // gradient: {
        //   type: 'linear',
        //   rotate: 22,
        //   colors: [
        //     { pos: 0, color: '#F4F4FE' },
        //     { pos: 50, color: '#D9D8FF' },
        //     { pos: 100, color: '#F6F6FF' },
        //   ],
        // },
        outline: {
          width: 1,
          color: '#D5D4FC',
          style: 'solid',
        },
      },
      {
        type: 'image',
        src: keyTitleIconRef.current.src,
        x: keyTitleIconRect.x,
        y: keyTitleIconRect.y,
        width: keyTitleIconRect.width,
        height: keyTitleIconRect.height,
      },
      {
        type: 'text',
        content: keyTitleTextRef.current.outerHTML,
        x: keyTitleTextRect.x + 10,
        y: keyTitleTextRect.y - 50,
        width: keyTitleTextRect.width,
        height: keyTitleTextRect.height,
      },
      ...Array.from(resultChildren).map((el) => {
        const rect = el.getBoundingClientRect()
        return {
          type: 'text',
          content: el.outerHTML.replace(/\n/g, ''),
          x: rect.x,
          y: rect.y - 40,
          width: rect.width,
          height: rect.height,
        } as SliderModels.PPTTextElement
      }),
      {
        type: 'shape',
        x: normBoxRect.x,
        y: normBoxRect.y,
        width: normBoxRect.width,
        height: normBoxRect.height,
        viewBox: [normBoxRect.width, normBoxRect.height],
        path: `M ${shapeInfo2.shapeHeightRatio} 0 L ${shapeInfo2.shapeWidthDelta} 0 Q ${shapeInfo2.shapeWidth} 0 ${shapeInfo2.shapeWidth} ${shapeInfo2.shapeHeightRatio} L ${shapeInfo2.shapeWidth} ${shapeInfo2.shapeHeightDelta} Q ${shapeInfo2.shapeWidth} ${shapeInfo2.shapeHeight} ${shapeInfo2.shapeWidthDelta} ${shapeInfo2.shapeHeight} L ${shapeInfo2.shapeHeightRatio} ${shapeInfo2.shapeHeight} Q 0 ${shapeInfo2.shapeHeight} 0 ${shapeInfo2.shapeHeightDelta} L 0 ${shapeInfo2.shapeHeightRatio} Q 0 0 ${shapeInfo2.shapeHeightRatio} 0 Z`,
        fixedRatio: false,
        rotate: 0,
        pathFormula: SliderModels.ShapePathFormulasKeys.ROUND_RECT,
        keypoints: [shapeInfo2.shapeRatio],
        fill: '#E8EBFF',
      },
      {
        type: 'text',
        content: normCntRef.current.outerHTML,
        x: normCntRect.x,
        y: normCntRect.y - 60,
        width: normCntRect.width,
        height: normCntRect.height,
      },
      {
        type: 'text',
        content: normDescRef.current.outerHTML,
        x: normDescRect.x,
        y: normDescRect.y - 50,
        width: normDescWidth,
        height: normDescRect.height,
      },
      {
        type: 'image',
        src: chartImage,
        x: chartRect.x,
        y: chartRect.y,
        width: chartRect.width,
        height: chartRect.height,
      },
    ]
    setElements(elements)
  }, [chartImage, resultChildren])

  useEffect(() => {
    if (!pageRect || !basicSlider || pageHeaderElements.length === 0 || elements.length === 0) {
      return
    }
    const _pageHeaderElements = pageHeaderElements.map((element) => {
      const { x, y } = element
      return {
        ...element,
        x: x - pageRect.left,
        y: y - pageRect.top,
      }
    })
    const _elements = elements.map((element) => {
      const { x, y } = element
      return {
        ...element,
        x: x - pageRect.left,
        y: y - pageRect.top,
      }
    })

    const slider: SliderModels.Slider = {
      ...basicSlider,
      elements: [..._pageHeaderElements, ...basicSlider.elements, ..._elements],
    }
    onSliderReady(slider, no)
  }, [pageRect, basicSlider, pageHeaderElements, elements])

  useEffect(() => {
    if (!chartRef.current || !chartRendered) {
      return
    }
    html2canvas(chartRef.current, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: null,
      logging: false,
    }).then((canvas) => {
      const base64Image = canvas.toDataURL('image/png')
      setChartImage(base64Image)
    })
  }, [chartRendered])

  useEffect(() => {
    if (!ratioFinished || !resultsRef.current) {
      return
    }
    const children = resultsRef.current.children
    for (const child of children) {
      const c = child as HTMLElement
      c.style.fontSize = `${24 * ratio}px`
      c.style.color = '#2A2C3F'
    }
    setResultChildren(children)
  }, [dimensionMetrics, ratio, ratioFinished])

  // useEffect(() => {
  //   if (resultsRef.current) {
  //     const resultsHeight = resultsRef.current.getBoundingClientRect().height
  //     if (resultsHeight > BOX_MAX_HEIGHT) {
  //       const ratio = BOX_MAX_HEIGHT / resultsHeight
  //       setRatio(ratio)
  //     }
  //     setRatioFinished(true)
  //   }
  // }, [])

  useEffect(() => {
    if (resultSize) {
      const resultHeight = resultSize.height
      if (resultHeight > BOX_MAX_HEIGHT) {
        setRatio((ratio) => ratio - 0.1)
      } else {
        setRatioFinished(true)
      }
    }
  }, [resultSize])

  return (
    <PptPage
      bgSrc={commonUtils.getImagePath('PDFDownload/pdf-page-7/PDF-bg.jpg')}
      no={no}
      onPageRender={onPageRender}
    >
      <div className={styles.pptPageMomentResults}>
        <PageHeader
          project={project}
          text={'03.' + t('dashboard.projectDetailPage.detailAnalysis')}
          onRender={onPageHeaderRender}
        />
        <div className={styles.titleContent}>
          <div className={styles.left}>
            <div
              ref={titleRef}
              className={styles.title}
              style={{
                fontWeight: 'bold',
                fontSize: 64,
                color: '#515366',
              }}
            >
              {titleMap[momentKey]}
            </div>
            <Divider
              type='vertical'
              style={{ height: 32, marginLeft: 24, marginRight: 24, position: 'relative', top: 8 }}
            />
            <div
              className={styles.rangeDesc}
              ref={descRef}
              style={{ fontSize: 24, color: '#2a2c3f' }}
            >
              {rangeDesc}
            </div>
          </div>
          <div className={styles.right} ref={normBoxRef} style={{ width: normBoxWidth, height: 72 }}>
            <span
              className={styles.score}
              style={{ fontSize: 48, fontWeight: 'bold', color: '#8463F9' }}
              ref={normCntRef}
            >
              {dimensionMetrics.score}
            </span>
            <Divider type='vertical' style={{ height: 22, marginLeft: 20, marginRight: 20 }} />
            <span
              className={styles.meanScore}
              ref={normDescRef}
              style={{ fontWeight: 'bold', fontSize: 24, color: '#2a2c3f' }}
            >
              {t('dashboard.projectDetailPage.storyboardView.norm')}: {dimensionMetrics.meanScore}
            </span>
          </div>
        </div>
        <div className={styles.resultContent}>
          <div className={styles.left}>
            <div className={styles.leftInner} ref={keyResultRef}>
              <div className={styles.title}>
                <img
                  ref={keyTitleIconRef}
                  style={{ width: 40, height: 40 }}
                  src={commonUtils.getImagePath('PDFDownload/robot-icon.png')}
                  alt=''
                />
                <span
                  ref={keyTitleTextRef}
                  style={{ fontSize: 32, color: '#6D54FA', fontWeight: 'bold' }}
                >
                  {t('dashboard.projectDetailPage.storyboardView.keyResult')}
                </span>
              </div>
              <div className={styles.results} ref={resultsRef}>
                <MarkdownRenderer
                  style={{ fontSize: 24 * ratio }}
                  markdownText={dimensionMetrics.results}
                />
              </div>
            </div>
          </div>
          <div className={styles.right} ref={chartRef}>
            <div className={styles.rightInner}>
              <ChartRangeView
                className={styles.chartRange}
                chartClassName={cls(styles.chart, isPortraitSplashCreative && styles.chartSplash)}
                project={project}
                ranges={dimensionMetrics.ranges}
                options={{ height: '92%', labelVisible: true }}
                thumbnailMode={isPortraitSplashCreative ? 'portrait-ppt' : isPortraitLayout ? 'portrait' : 'default'}
                onRender={() => {
                  setTimeout(() => {
                    setChartRendered(true)
                  }, 1000)
                }}
              />
              <div className={styles.legendBox}>
                <LegendDot className={styles.legend} style={{ justifyContent: 'space-around' }} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </PptPage>
  )
}

export default PptPageMomentResults
